// The curriculum questions' rules, shared by the stepper on the results page and
// the filters modal: which choices apply, what an answer clears, and long lists.

// The sentences built here, in the page's language, from the page that asks the questions.
let parsed = {}
export const copy = () => {
  const text = document.querySelector("[data-curriculum-copy]")?.dataset.curriculumCopy || "{}"
  if (parsed.text !== text) parsed = { text, copy: JSON.parse(text) }
  return parsed.copy
}
export const format = (text, values) => text.replace(/%\{(\w+)\}/g, (_, key) => values[key])
export const plural = (forms, count) => count === 1 ? forms.one : forms.other

// A group of choices applies while the answer it hangs off is picked. One that
// stops applying loses its picks, which may in turn stop others applying.
export function cascade(root, groups) {
  const checked = value => root.querySelector(`input[value="${value}"]`)?.checked
  let changed = true
  while (changed) {
    changed = false
    groups.forEach(group => {
      const shown = (group.dataset.parent || "").split(" ").filter(Boolean).every(checked)
      group.classList.toggle("hidden", !shown)
      if (shown) return

      group.querySelectorAll("input:checked").forEach(input => { input.checked = false; changed = true })
    })
  }
}

export function visibleOptions(step, groups) {
  return groups.filter(group => step.contains(group) && !group.classList.contains("hidden"))
    .flatMap(group => [...group.querySelectorAll("input")])
}

// Long lists show their first few. Folding keeps the picked one; nothing else
// hides until the list is folded again.
export function toggleMore(rows) {
  const expanding = rows.some(row => row.classList.contains("!hidden"))
  rows.forEach(row => row.classList.toggle("!hidden", !expanding && !row.querySelector("input:checked")))
}

export function revealPicked(rows) {
  rows.filter(row => row.querySelector("input:checked")).forEach(row => row.classList.remove("!hidden"))
}

// "Show more" while anything is folded away, "Show less" once it's all out,
// and no button when there's nothing to show or fold.
export function updateMoreButtons(rows) {
  new Set(rows.map(row => row.parentElement)).forEach(list => {
    const listed = rows.filter(row => list.contains(row))
    const folded = listed.some(row => row.classList.contains("!hidden"))
    const button = list.querySelector("button")
    button.hidden = !folded && listed.every(row => row.querySelector("input:checked"))
    button.querySelector("span").textContent = folded ? copy().show_more : copy().show_less
  })
}

// The Year level filter and the curriculum's stage both say which students. They stay
// in step: the latest pick wins, and a hint says what changed. A stage answered from
// the year levels is marked, so changing the year levels can answer it again.
export const pickedYears = root => [...root.querySelectorAll("input[name='year_level[]']:checked")].map(input => Number(input.value))

const yearsOf = stage => (stage.closest("label").dataset.years || "").split(" ").filter(Boolean).map(Number)
const fits = (stage, years) => yearsOf(stage).some(year => years.includes(year))
const label = stage => stage.closest("label").dataset.label
export const midSentence = text => text.charAt(0).toLowerCase() + text.slice(1)
const term = stage => stage.closest("[data-term]")?.dataset.term

export const listOf = items => items.length > 1 ? `${items.slice(0, -1).join(", ")}${copy().and}${items.at(-1)}` : items[0]

// "year 2", "years 10 and 11", "reception and year 1", as the Year level filter names them.
function yearsPhrase(root, years) {
  const { years: words } = copy()
  const names = [...years].sort((a, b) => a - b).map(year => {
    const name = root.querySelector(`input[name='year_level[]'][value='${year}']`)?.closest("label")?.querySelector("span.flex-1")?.textContent.trim()
    return { year, name: midSentence(name || (year === 0 ? words.reception : format(words.year, { year }))) }
  })
  if (names.length > 1 && names.every(({ year, name }) => name === midSentence(format(words.year, { year })))) {
    const numbers = names.map(({ year }) => year)
    const run = numbers.every((number, i) => i === 0 || number === numbers[i - 1] + 1)
    return run ? format(words.run, { from: numbers[0], to: numbers.at(-1) }) : format(words.list, { list: listOf(numbers) })
  }
  return listOf(names.map(({ name }) => name))
}

// Picking a stage that none of the year levels fall in takes the year levels off.
export function removeClashingYears(root, stage) {
  delete stage.dataset.fromYears
  const years = pickedYears(root)
  if (!years.length || fits(stage, years)) return null

  const hint = format(copy().hints.removed, { stage: label(stage), years: yearsPhrase(root, years) })
  const inputs = [...root.querySelectorAll("input[name='year_level[]']:checked")]
  inputs.forEach(input => { input.checked = false })
  inputs.at(-1).dispatchEvent(new CustomEvent("change", { bubbles: true, detail: { fromStage: true } }))
  return hint
}

// After the year levels change: the stage stays while it fits them. Otherwise the stage
// they point to is picked, when exactly one does, or it's cleared. Returns the hint and
// what kind it is ("based" or "cleared") and whether the stage answer changed.
export function followYears(root, stages) {
  const years = pickedYears(root)
  const picked = stages.find(stage => stage.checked)
  const based = stage => format(copy().hints.based, { stage: label(stage), years: yearsPhrase(root, years) })
  if (picked && (!years.length || fits(picked, years))) {
    if (!years.length) delete picked.dataset.fromYears
    return { hint: picked.dataset.fromYears ? based(picked) : null, kind: "based", changed: false }
  }

  const fitting = years.length ? stages.filter(stage => fits(stage, years)) : []
  const next = fitting.length === 1 ? fitting[0] : null
  const own = picked && !picked.dataset.fromYears
  stages.forEach(stage => { stage.checked = false; delete stage.dataset.fromYears })
  if (next) {
    next.checked = true
    next.dataset.fromYears = "true"
    return { hint: based(next), kind: "based", changed: true }
  }
  const hint = own ? format(copy().hints.cleared, { stage: label(picked), years: yearsPhrase(root, years), term: term(picked) }) : null
  return { hint, kind: "cleared", changed: !!picked }
}
